import React from "react";
import { X } from "lucide-react";

import { ListInput } from "./ListInput";
import { controlCls } from "@/shared/components/teams-ui";
import { LIMITS, TYPE_NAMES, isChoice, withType } from "../blocks";
import { LimitInput } from "./LimitInput";

import type { Question, QuestionType } from "@/shared/types";

/* One question: what it asks, its type, whether it's required, and what the type takes —
   its choices, its limits. A question keeps its `key` through a rename or a new type, so
   the answers people gave stay with it (and are asked again if they no longer fit). */
export function QuestionRow({ question, number, onChange, onRemove }: {
  question: Question;
  number: number;
  onChange: (question: Question) => void;
  onRemove: () => void;
}) {
  const limits = LIMITS[question.type];
  const name = question.label.trim() || `question ${number}`;
  return (
    <div className="rounded-[10px] bg-[var(--surface-2)] p-2 space-y-2">
      <div className="flex items-center gap-2">
        <input
          aria-label={`Question ${number}`}
          value={question.label}
          maxLength={100}
          placeholder="What to ask, e.g. Company"
          onChange={(e) => onChange({ ...question, label: e.target.value })}
          className={`${controlCls} flex-1 min-w-0`}
        />
        <button
          type="button"
          aria-label={`Remove ${name}`}
          onClick={onRemove}
          className="press w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
        >
          <X className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
      </div>
      <select
        aria-label={`Type of ${name}`}
        value={question.type}
        onChange={(e) => onChange(withType(question, e.target.value as QuestionType))}
        className={`${controlCls} w-full`}
      >
        {(Object.keys(TYPE_NAMES) as QuestionType[]).map((type) => (
          <option key={type} value={type}>{TYPE_NAMES[type]}</option>
        ))}
      </select>
      {isChoice(question.type) && (
        <ListInput
          key={question.type}
          label={`Choices of ${name}`}
          placeholder="One choice per line"
          value={question.options ?? []}
          onChange={(options) => onChange({ ...question, options })}
        />
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <label className="flex items-center gap-1.5 text-[12px] text-[var(--text-secondary)] whitespace-nowrap">
          <input
            type="checkbox"
            checked={question.required}
            onChange={(e) => onChange({ ...question, required: e.target.checked })}
            className="accent-[var(--brand)]"
          />
          Required
        </label>
        {limits && (
          <React.Fragment key={question.type}>
            <LimitInput label={limits.min} value={question.min} onChange={(min) => onChange({ ...question, min })} />
            <LimitInput label={limits.max} value={question.max} onChange={(max) => onChange({ ...question, max })} />
          </React.Fragment>
        )}
      </div>
    </div>
  );
}
